/**
 * Savings Goals Service
 * Business logic for savings goals operations
 */

import { 
  API_ENDPOINTS, 
  makeAuthenticatedApiRequest, 
  type ApiResult, 
  type CreateSavingsGoalRequest,
  type UpdateSavingsGoalRequest,
  type CreateSavingsGoalResponse,
  type GetSavingsGoalsResponse,
  type UpdateSavingsGoalResponse,
  type DeleteSavingsGoalResponse
} from './apiConfig';
import { 
  validateCreateGoalRequest, 
  validateUpdateGoalRequest, 
  validateAccessToken, 
  validateGoalId,
  type CreateSavingsGoalRequest as ValidatedCreateRequest,
  type UpdateSavingsGoalRequest as ValidatedUpdateRequest
} from './validators';

/**
 * Create a new savings goal via API
 * Goal name will be trimmed and validated for length and content
 */
export async function createSavingsGoal(
  goalData: ValidatedCreateRequest,
  accessToken: string
): Promise<ApiResult<CreateSavingsGoalResponse>> {
  // Input validation
  validateCreateGoalRequest(goalData);
  validateAccessToken(accessToken);
  
  const payload: CreateSavingsGoalRequest = {
    name: goalData.name.trim(),
    targetAmount: goalData.targetAmount,
    duration: goalData.duration
  };
  
  return makeAuthenticatedApiRequest(API_ENDPOINTS.SAVINGS_GOALS.CREATE, payload, accessToken);
}

/**
 * Get all savings goals for the authenticated user via API
 */
export async function getSavingsGoals(
  accessToken: string
): Promise<ApiResult<GetSavingsGoalsResponse>> {
  // Input validation
  validateAccessToken(accessToken);
  
  return makeAuthenticatedApiRequest(API_ENDPOINTS.SAVINGS_GOALS.GET_ALL, undefined, accessToken);
}

/**
 * Update an existing savings goal via API
 * Supports partial updates - only provided fields will be updated
 */
export async function updateSavingsGoal(
  goalId: string,
  updates: ValidatedUpdateRequest,
  accessToken: string
): Promise<ApiResult<UpdateSavingsGoalResponse>> {
  // Input validation
  validateGoalId(goalId);
  validateUpdateGoalRequest(updates);
  validateAccessToken(accessToken);
  
  const payload: UpdateSavingsGoalRequest = { ...updates };
  
  // Trim name if provided
  if (payload.name) {
    payload.name = payload.name.trim();
  }
  
  const endpoint = API_ENDPOINTS.SAVINGS_GOALS.UPDATE(goalId);
  
  return makeAuthenticatedApiRequest(endpoint, payload, accessToken);
}

/**
 * Delete a savings goal via API
 */
export async function deleteSavingsGoal(
  goalId: string,
  accessToken: string
): Promise<ApiResult<DeleteSavingsGoalResponse>> {
  // Input validation
  validateGoalId(goalId);
  validateAccessToken(accessToken);
  
  const endpoint = API_ENDPOINTS.SAVINGS_GOALS.DELETE(goalId);
  
  return makeAuthenticatedApiRequest(endpoint, undefined, accessToken);
}

/**
 * Pause a savings goal via API
 * Sets the goal status to 'paused' and records the pause timestamp
 */
export async function pauseSavingsGoal(
  goalId: string,
  accessToken: string
): Promise<ApiResult<UpdateSavingsGoalResponse>> {
  // Input validation
  validateGoalId(goalId);
  validateAccessToken(accessToken);
  
  const endpoint = API_ENDPOINTS.SAVINGS_GOALS.PAUSE(goalId);
  
  return makeAuthenticatedApiRequest(endpoint, undefined, accessToken);
}

/**
 * Resume a paused savings goal via API
 * Sets the goal status to 'active', calculates paused time, and adjusts allocations
 */
export async function resumeSavingsGoal(
  goalId: string,
  accessToken: string
): Promise<ApiResult<UpdateSavingsGoalResponse>> {
  // Input validation
  validateGoalId(goalId);
  validateAccessToken(accessToken);
  
  const endpoint = API_ENDPOINTS.SAVINGS_GOALS.RESUME(goalId);
  
  return makeAuthenticatedApiRequest(endpoint, undefined, accessToken);
}

/**
 * Update goal progress via API
 * Updates the actualProgress field for manual progress tracking
 */
export async function updateGoalProgress(
  goalId: string,
  actualProgress: number,
  accessToken: string
): Promise<ApiResult<UpdateSavingsGoalResponse>> {
  // Input validation
  validateGoalId(goalId);
  validateAccessToken(accessToken);
  
  if (typeof actualProgress !== 'number' || actualProgress < 0) {
    throw new Error('Actual progress must be a non-negative number');
  }
  
  const updates: ValidatedUpdateRequest = {
    actualProgress
  };
  
  return updateSavingsGoal(goalId, updates, accessToken);
}

/**
 * Mark a goal as completed via API
 * Sets the goal status to 'completed'
 */
export async function markGoalCompleted(
  goalId: string,
  accessToken: string
): Promise<ApiResult<UpdateSavingsGoalResponse>> {
  // Input validation
  validateGoalId(goalId);
  validateAccessToken(accessToken);
  
  const updates: ValidatedUpdateRequest = {
    status: 'completed'
  };
  
  return updateSavingsGoal(goalId, updates, accessToken);
}